import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiService } from '@/lib/services/api'
import { DOCUMENTS_ENDPOINTS } from '@/api/endpoints'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Folder,
  FileText,
  ChevronRight,
  Home,
  Loader2,
  AlertCircle,
} from 'lucide-react'

export const CONTACTS_BASE_PATH = 'Piketinformatie/01 Contactgegevens'

interface BrowseItem {
  id: string
  name: string
  path: string
  folderPath: string
  webUrl?: string
  size?: number
  lastModified?: string
  isFolder: boolean
}

interface BrowseResponse {
  data: {
    documents: BrowseItem[]
    count: number
    folder_path: string
  }
}

interface SharePointFilePickerDialogProps {
  open: boolean
  onClose: () => void
  onSelect: (path: string) => void
  basePath?: string
  startPath?: string
  currentPath?: string
}

const generateBreadcrumbs = (basePath: string, targetPath: string): BrowseItem[] => {
  const normalizedTarget = targetPath.startsWith(basePath) ? targetPath : basePath
  const targetParts = normalizedTarget.split('/').filter(Boolean)
  const breadcrumbs: BrowseItem[] = []

  let accumulated = ''
  targetParts.forEach((part, index) => {
    accumulated = index === 0 ? part : `${accumulated}/${part}`
    // Skip the first part (showed by base button as root)
    if (index > 0) {
      breadcrumbs.push({
        id: accumulated,
        name: part,
        path: accumulated,
        folderPath: accumulated,
        isFolder: true,
      })
    }
  })

  return breadcrumbs
}

export function SharePointFilePickerDialog({
  open,
  onClose,
  onSelect,
  basePath = CONTACTS_BASE_PATH,
  startPath,
}: SharePointFilePickerDialogProps) {
  const { t } = useTranslation()

  const [items, setItems] = useState<BrowseItem[]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<BrowseItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadFolder = useCallback(async (path: string) => {
    setIsLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ folder_path: path })
      const resp = await apiService.get<BrowseResponse>(
        `${DOCUMENTS_ENDPOINTS.BROWSE}?${params}`
      )
      setItems(resp.data.documents)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('integration.sp_file_picker_error'))
      setItems([])
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (open) {
      const initialPath = startPath && startPath.length > 0 ? startPath : basePath
      const breadcrumbPath = startPath && startPath.length > 0 ? startPath : basePath
      setBreadcrumbs(generateBreadcrumbs(basePath, breadcrumbPath))
      loadFolder(initialPath)
    } else {
      // Reset state when closed
      setItems([])
      setError(null)
      setBreadcrumbs([])
    }
  }, [open, basePath, startPath, loadFolder])

  const handleFolderClick = async (folder: BrowseItem) => {
    setBreadcrumbs((prev) => [...prev, folder])
    await loadFolder(folder.path)
  }

  const handleBreadcrumbClick = async (index: number) => {
    if (index === -1) {
      setBreadcrumbs([])
      await loadFolder(basePath)
    } else {
      const next = breadcrumbs.slice(0, index + 1)
      setBreadcrumbs(next)
      await loadFolder(next[next.length - 1].path)
    }
  }

  const handleFileSelect = (file: BrowseItem) => {
    onSelect(file.path)
    onClose()
  }

  const folders = items.filter((i) => i.isFolder)
  const files = items.filter((i) => !i.isFolder)
  const baseLabel = basePath.split('/')[0] ?? basePath

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>{t('integration.sp_file_picker_title')}</DialogTitle>
          <DialogDescription>
            {t('integration.sp_file_picker_description', { basePath })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0 overflow-hidden gap-3">
          {/* Breadcrumb */}
          <nav
            aria-label="Mapnavigatie"
            className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap"
          >
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              disabled={isLoading}
              className={cn(
                'flex items-center gap-1.5 px-1.5 py-0.5 rounded hover:bg-gray-100 transition-colors text-xs',
                breadcrumbs.length === 0 && 'text-foreground font-medium'
              )}
            >
              <Home className="w-3.5 h-3.5" />
              <span>{baseLabel}</span>
            </button>
            {breadcrumbs.map((crumb, i) => (
              <div key={`${crumb.id}-${i}`} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3 shrink-0 text-gray-300" />
                <button
                  onClick={() => handleBreadcrumbClick(i)}
                  disabled={isLoading}
                  className={cn(
                    'px-1 py-0.5 rounded hover:bg-gray-100 transition-colors truncate max-w-40 text-xs',
                    i === breadcrumbs.length - 1 && 'text-foreground font-medium'
                  )}
                  title={crumb.name}
                >
                  {crumb.name}
                </button>
              </div>
            ))}
          </nav>

          {/* File list */}
          <div className="flex-1 overflow-y-auto border rounded-lg min-h-[200px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{t('documents.loading')}</span>
              </div>
            ) : error ? (
              <div className="flex items-start gap-2 p-4 m-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                {t('documents.noDocuments')}
              </div>
            ) : (
              <table className="w-full text-sm">
                <tbody className="divide-y">
                  {folders.map((folder) => (
                    <tr
                      key={folder.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors group"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <Folder className="w-4 h-4 text-blue-500 shrink-0" />
                          <span className="font-medium text-foreground group-hover:text-blue-600 transition-colors">
                            {folder.name}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-300 shrink-0 ml-auto" />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {files.map((file) => (
                    <tr
                      key={file.id}
                      className="hover:bg-blue-50 cursor-pointer transition-colors group"
                      onClick={() => handleFileSelect(file)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-foreground group-hover:text-blue-700 truncate">
                            {file.name}
                          </span>
                          <span className="ml-auto text-xs text-blue-600 opacity-0 group-hover:opacity-100 shrink-0">
                            {t('integration.sp_file_picker_select')}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Item count */}
          {!isLoading && !error && items.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {folders.length > 0 && `${folders.length} map${folders.length !== 1 ? 'pen' : ''}`}
              {folders.length > 0 && files.length > 0 && ', '}
              {files.length > 0 && `${files.length} bestand${files.length !== 1 ? 'en' : ''}`}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
